import React, { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from 'recharts';
import { PackageCheck, Shirt, Scale, Truck, Loader2 } from 'lucide-react';
import KpiCard from '../../components/KpiCard';
import { computeAlistamientosIndicadores } from '../../helpers/registrosIndicadores';
import { ChartBox, EmptyChart, HorizontalBars, COLORS, fmtNum, dayAxisProps } from './chartParts';

const fmtKg = (n) => `${fmtNum(n, 1)} kg`;

const ESTADO_COLORS = {
  Entregado: COLORS.green,
  'En Ruta': COLORS.lightBlue,
  Alistado: COLORS.navy,
  Pendiente: COLORS.amber,
  Cancelado: COLORS.coral,
};

// Indicadores y gráficas de los alistamientos del periodo.
const AlistamientosIndicadoresSection = ({ registros }) => {
  const { data, loading, error } = registros;
  const ind = useMemo(() => computeAlistamientosIndicadores(data), [data]);
  const k = ind.kpis;
  const lenceria = ind.porZona.find((z) => z.nombre === 'Lenceria');
  const toallas = ind.porZona.find((z) => z.nombre === 'Toallas');

  if (error) {
    return <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm rounded-xl p-4">{error}</div>;
  }
  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Calculando indicadores…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={<PackageCheck className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Alistamientos"
          value={fmtNum(k.total)}
          sub={{ label: 'Bolsas', value: fmtNum(k.bolsas) }}
          sub2={{ label: 'Días con actividad', value: fmtNum(k.diasConDatos) }}
        />
        <KpiCard
          icon={<Shirt className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Prendas alistadas"
          value={fmtNum(k.prendas)}
          unit="prendas"
          sub={{ label: 'Lencería / Toallas', value: `${fmtNum(lenceria?.prendas)} / ${fmtNum(toallas?.prendas)}` }}
          sub2={{ label: 'Promedio por alistamiento', value: fmtNum(k.promPrendas, 1) }}
        />
        <KpiCard
          icon={<Scale className="h-3.5 w-3.5 text-[#0F3460]" />}
          label="Kilos alistados"
          value={fmtNum(k.kilos, 1)}
          unit="kg"
          sub={{ label: 'Lencería / Toallas', value: `${fmtKg(lenceria?.kilos)} / ${fmtKg(toallas?.kilos)}` }}
          sub2={{ label: 'Promedio por alistamiento', value: fmtKg(k.promKg) }}
        />
        <KpiCard
          icon={<Truck className="h-3.5 w-3.5 text-[#0E9F6E]" />}
          label="Entregados"
          value={fmtNum(k.pctEntregados, 1)}
          unit="%"
          status="ok"
          showTag={false}
          sub={{ label: 'Alistamientos entregados', value: `${fmtNum(k.entregados)} de ${fmtNum(k.total)}` }}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartBox title="Prendas por día" hint="Barras apiladas por zona · Línea: kilos alistados.">
          {ind.porDia.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer>
              <ComposedChart data={ind.porDia} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis {...dayAxisProps(ind.porDia.length)} />
                <YAxis yAxisId="prendas" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis yAxisId="kg" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={(l) => `Día ${l}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="prendas" dataKey="Lenceria" name="Lencería" stackId="z" fill={COLORS.navy} />
                <Bar yAxisId="prendas" dataKey="Toallas" name="Toallas" stackId="z" fill={COLORS.lightBlue} radius={[3, 3, 0, 0]} />
                <Line yAxisId="kg" type="monotone" dataKey="kilos" name="Kilos" stroke={COLORS.coral} strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </ChartBox>

        <ChartBox title="Top 10 prendas" hint="Prendas más alistadas en el periodo (cantidad).">
          <HorizontalBars data={ind.topPrendas} dataKey="cantidad" name="Cantidad" />
        </ChartBox>

        <ChartBox title="Top clientes por prendas" hint="Los 10 clientes con más prendas alistadas.">
          <HorizontalBars data={ind.porCliente} dataKey="prendas" name="Prendas" color={COLORS.lightBlue} />
        </ChartBox>

        <ChartBox title="Estado de los alistamientos" hint="Cantidad de alistamientos por estado.">
          {ind.porEstado.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer>
              <BarChart data={ind.porEstado} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="nombre" tick={{ fontSize: 11 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="cantidad" name="Alistamientos" radius={[3, 3, 0, 0]}>
                  {ind.porEstado.map((e) => (
                    <Cell key={e.nombre} fill={ESTADO_COLORS[e.nombre] || COLORS.navy} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartBox>
      </div>
    </div>
  );
};

export default AlistamientosIndicadoresSection;
